import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as moment from 'moment';
import { formatAbbreviatedDuration } from 'src/util/formatters';

export interface TimeBlockEditorDialogInput {
  taskName: string;
  startTime: moment.Moment;
  endTime: moment.Moment | null;
  lowerBound: moment.Moment | null;
  upperBound: moment.Moment | null;
}

export interface TimeBlockEditorDialogResult {
  startTime: moment.Moment;
  endTime: moment.Moment | null;
}

@Component({
  selector: 'app-time-block-editor-dialog',
  templateUrl: './time-block-editor-dialog.component.html',
  styleUrls: ['./time-block-editor-dialog.component.scss']
})
export class TimeBlockEditorDialogComponent implements OnInit {
  taskName: string;
  originalStart: moment.Moment;
  originalEnd: moment.Moment | null;

  get startLowerBound(): moment.Moment | undefined { return this.input.lowerBound || undefined; }
  get startUpperBound(): moment.Moment {
    return (this.selectedEnd || this.input.upperBound) && // null if both boundary sources are null, otherwise
      moment(
        this.selectedEnd && this.input.upperBound // if both sources are non-null, take the min
          ? moment.min(this.selectedEnd, this.input.upperBound)
          : (this.selectedEnd || this.input.upperBound) // otherwise take the one that isn't null
      ) || moment();
  }

  get endLowerBound(): moment.Moment { return this.selectedStart; }
  get endUpperBound(): moment.Moment { return this.input.upperBound || moment() }

  _selectedStart!: moment.Moment;
  get selectedStart(): moment.Moment { return this._selectedStart; }
  set selectedStart(value: moment.Moment) { this._selectedStart = value; }
  _selectedEnd!: moment.Moment | null;
  get selectedEnd(): moment.Moment | null { return this._selectedEnd; }
  set selectedEnd(value: moment.Moment | null) { this._selectedEnd = value; }

  get formattedDuration(): string {
    return !this.selectedEnd ? '' : formatAbbreviatedDuration(moment.duration(this.selectedEnd.diff(this.selectedStart)))
  }
  get durationDelta(): string {
    const originalDuration = moment.duration(this.originalStart.diff(this.originalEnd));
    const selectedDuration = moment.duration(this._selectedStart.diff(this._selectedEnd));
    const delta = originalDuration.subtract(selectedDuration);

    const sign = delta.asMilliseconds() > 0 ? 'longer' : 'shorter';
    return delta.asMilliseconds() === 0 ? 'no change' : `${sign} by ${formatAbbreviatedDuration(delta)}`;
  }

  constructor(
    public dialogRef: MatDialogRef<TimeBlockEditorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private input: TimeBlockEditorDialogInput
  ) {
    this.originalStart = moment(input.startTime);
    this.originalEnd = input.endTime && moment(input.endTime) || null;
    this.taskName = input.taskName;

    this.selectedStart = moment(this.originalStart);
    this.selectedEnd = this.originalEnd && moment(this.originalEnd);
  }

  ngOnInit(): void {
  }

  get canSubmit(): boolean {
    return !this.selectedStart.isSame(this.input.startTime) || !this.selectedEnd?.isSame(this.input.endTime);
  }

  getResult = (): TimeBlockEditorDialogResult => {
    return {
      startTime: this.selectedStart,
      endTime: this.selectedEnd,
    };
  }
}
